import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  FolderOpen, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Trash2,
  ArrowRight,
  FileText,
  Brain
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { label: "Taslak", color: "bg-muted text-muted-foreground", icon: FileText },
  analyzing: { label: "Analiz Ediliyor", color: "bg-blue-500/20 text-blue-400", icon: Loader2 },
  completed: { label: "Tamamlandı", color: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
  error: { label: "Hata", color: "bg-destructive/20 text-destructive", icon: AlertCircle },
};

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteProject, setDeleteProject] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        withCredentials: true
      });
      setProjects(response.data);
    } catch (error) {
      toast.error("Projeler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch current user
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    fetchProjects();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchProjects, 10000);
    return () => clearInterval(interval);
  }, [user]);

  const handleDelete = async () => {
    if (!deleteProject) return;
    try {
      await axios.delete(`${API}/projects/${deleteProject}`, {
        withCredentials: true
      });
      toast.success("Proje silindi");
      setProjects(projects.filter(p => p.id !== deleteProject));
    } catch (error) {
      toast.error("Proje silinirken hata oluştu");
    } finally {
      setDeleteProject(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="p-8 lg:p-12 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Hoş geldin, {user?.name}. Projelerinizi yönetin.
          </p>
        </div>
        <Button 
          onClick={() => navigate("/new-project")} 
          className="btn-glow"
          data-testid="new-project-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Proje
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: "Toplam Proje", value: projects.length, icon: FolderOpen, color: "text-primary" },
          { label: "Analiz Ediliyor", value: projects.filter(p => p.status === "analyzing").length, icon: Loader2, color: "text-blue-400" },
          { label: "Tamamlanan", value: projects.filter(p => p.status === "completed").length, icon: CheckCircle2, color: "text-emerald-400" },
          { label: "Taslak", value: projects.filter(p => p.status === "draft").length, icon: Clock, color: "text-muted-foreground" },
        ].map((stat, i) => (
          <Card key={i} className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl bg-secondary flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Projects Grid */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Projeler</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : projects.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-secondary mx-auto mb-4 flex items-center justify-center">
                <Brain className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Henüz proje yok</h3>
              <p className="text-muted-foreground mb-4">
                İlk projenizi oluşturarak iş analizi yapmaya başlayın.
              </p>
              <Button onClick={() => navigate("/new-project")} data-testid="empty-new-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                Proje Oluştur
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => {
              const status = statusConfig[project.status] || statusConfig.draft;
              const StatusIcon = status.icon;
              
              return (
                <Card 
                  key={project.id} 
                  className="glass-card card-interactive animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`project-card-${project.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg line-clamp-1">{project.name}</CardTitle>
                      <Badge className={`${status.color} flex items-center gap-1`}>
                        <StatusIcon className={`w-3 h-3 ${project.status === "analyzing" ? "animate-spin" : ""}`} />
                        {status.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {project.description || project.idea_text?.substring(0, 100) || "Açıklama yok"}
                    </p>
                    
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {project.documents?.length || 0} doküman
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(project.created_at)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/project/${project.id}`)}
                        data-testid={`open-project-${project.id}`}
                      >
                        Aç
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteProject(project.id)}
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProject} onOpenChange={() => setDeleteProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projeyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu projeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm analizler silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">İptal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
